import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Banner from '../../components/Banner';
import { useMessage } from '../../components/MessageProvider';
import type { AdminPlan } from '../../lib/apiClient';
import { useApi } from '../../hooks/useApi';
import { useNavigate } from 'react-router-dom';

type EditableAdminPlan = {
  plan_code: 'free' | 'pro';
  display_name: string;
  max_documents: string;
  max_taccounts_per_document: string;
  max_transactions_per_document: string;
  month_price_usd: string;
  year_price_usd: string;
  free_trial_days: string;
};

function toEditablePlan(plan: AdminPlan): EditableAdminPlan {
  return {
    plan_code: plan.plan_code === 'pro' ? 'pro' : 'free',
    display_name: plan.display_name,
    max_documents: String(plan.max_documents ?? ''),
    max_taccounts_per_document: String(plan.max_taccounts_per_document ?? ''),
    max_transactions_per_document: String(plan.max_transactions_per_document ?? ''),
    month_price_usd: Number(plan.month_price_usd ?? 0).toFixed(2),
    year_price_usd: Number(plan.year_price_usd ?? 0).toFixed(2),
    free_trial_days: String(plan.free_trial_days ?? 0),
  };
}

function sortPlans(plans: EditableAdminPlan[]): EditableAdminPlan[] {
  return [...plans].sort((a, b) => {
    if (a.plan_code === b.plan_code) {
      return 0;
    }
    return a.plan_code === 'free' ? -1 : 1;
  });
}

export default function Admin() {
  const navigate = useNavigate();
  const { getRuntimeFlags, updateRuntimeFlags, getBillingDiagnostics, getAdminPlans, updateAdminPlans } = useApi();
  const { showSuccess } = useMessage();
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [savedRequireWhitelistCookie, setSavedRequireWhitelistCookie] = React.useState<boolean | null>(null);
  const [draftRequireWhitelistCookie, setDraftRequireWhitelistCookie] = React.useState(false);
  const [isDiagnosticLoading, setIsDiagnosticLoading] = React.useState(false);
  const [diagnosticError, setDiagnosticError] = React.useState<string | null>(null);
  const [diagnosticData, setDiagnosticData] = React.useState<any | null>(null);
  const [isPlansLoading, setIsPlansLoading] = React.useState(true);
  const [plansLoadError, setPlansLoadError] = React.useState<string | null>(null);
  const [isPlansEditing, setIsPlansEditing] = React.useState(false);
  const [isPlansSaving, setIsPlansSaving] = React.useState(false);
  const [plansDraft, setPlansDraft] = React.useState<EditableAdminPlan[]>([]);
  const [plansEditStartSnapshot, setPlansEditStartSnapshot] = React.useState<EditableAdminPlan[] | null>(null);

  const hasPendingChange =
    savedRequireWhitelistCookie !== null &&
    draftRequireWhitelistCookie !== savedRequireWhitelistCookie;

  const loadRuntimeFlags = React.useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);

    const result = await getRuntimeFlags();
    if ('error' in result) {
      setLoadError(result.error.message || 'Failed to load runtime flags.');
      setIsLoading(false);
      return;
    }

    const nextValue = result.data.flags?.require_whitelist_cookie ?? true;
    setSavedRequireWhitelistCookie(nextValue);
    setDraftRequireWhitelistCookie(nextValue);
    setIsLoading(false);
  }, [getRuntimeFlags]);

  const loadPlans = React.useCallback(async () => {
    setIsPlansLoading(true);
    setPlansLoadError(null);

    const result = await getAdminPlans({
      shouldShowError: () => false,
    });

    if ('error' in result) {
      setPlansLoadError(result.error.message || 'Failed to load plans.');
      setIsPlansLoading(false);
      return;
    }

    const normalizedPlans = sortPlans(result.data.plans.map(toEditablePlan));
    setPlansDraft(normalizedPlans);
    setIsPlansEditing(false);
    setPlansEditStartSnapshot(null);
    setIsPlansLoading(false);
  }, [getAdminPlans]);

  React.useEffect(() => {
    void loadRuntimeFlags();
  }, [loadRuntimeFlags]);

  React.useEffect(() => {
    void loadPlans();
  }, [loadPlans]);

  const handleApply = async () => {
    if (!hasPendingChange) {
      return;
    }

    setIsSaving(true);
    const result = await updateRuntimeFlags({
      flags: {
        require_whitelist_cookie: draftRequireWhitelistCookie,
      },
    });

    if ('data' in result) {
      const persistedValue = result.data.flags?.require_whitelist_cookie ?? draftRequireWhitelistCookie;
      setSavedRequireWhitelistCookie(persistedValue);
      setDraftRequireWhitelistCookie(persistedValue);
      showSuccess('Whitelist runtime flag updated.');
    }

    setIsSaving(false);
  };

  const handleRunBillingDiagnostic = async () => {
    setIsDiagnosticLoading(true);
    setDiagnosticError(null);

    const result = await getBillingDiagnostics({
      shouldShowError: () => false,
    });

    if ('error' in result) {
      setDiagnosticError(result.error.message || 'Failed to load billing diagnostics.');
      setDiagnosticData(null);
      setIsDiagnosticLoading(false);
      return;
    }

    setDiagnosticData(result.data);
    setIsDiagnosticLoading(false);
  };

  const handlePlanFieldChange = (
    planCode: 'free' | 'pro',
    field: keyof EditableAdminPlan,
    value: string,
  ) => {
    setPlansDraft((current) => current.map((plan) => (
      plan.plan_code === planCode
        ? {
          ...plan,
          [field]: value,
        }
        : plan
    )));
  };

  const handleEditPlans = () => {
    setPlansEditStartSnapshot(plansDraft.map((plan) => ({ ...plan })));
    setIsPlansEditing(true);
  };

  const handleCancelPlansEdit = () => {
    if (plansEditStartSnapshot) {
      setPlansDraft(plansEditStartSnapshot.map((plan) => ({ ...plan })));
    }
    setIsPlansEditing(false);
    setPlansEditStartSnapshot(null);
  };

  const handleSavePlans = async () => {
    setIsPlansSaving(true);

    const payloadPlans = plansDraft.map((plan) => ({
      plan_code: plan.plan_code,
      max_documents: Number.parseInt(plan.max_documents, 10),
      max_taccounts_per_document: Number.parseInt(plan.max_taccounts_per_document, 10),
      max_transactions_per_document: Number.parseInt(plan.max_transactions_per_document, 10),
      month_price_usd: Number.parseFloat(plan.month_price_usd),
      year_price_usd: Number.parseFloat(plan.year_price_usd),
      free_trial_days: Number.parseInt(plan.free_trial_days, 10),
    }));

    const result = await updateAdminPlans(
      {
        plans: payloadPlans,
      },
      {
        shouldShowError: () => true,
      },
    );

    if ('data' in result) {
      const normalizedPlans = sortPlans(result.data.plans.map(toEditablePlan));
      setPlansDraft(normalizedPlans);
      setIsPlansEditing(false);
      setPlansEditStartSnapshot(null);
      showSuccess('Plans updated. Changes take effect on next API call or refresh.');
    }

    setIsPlansSaving(false);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: (theme) => (theme.palette.mode === 'light' ? '#f4f6f7' : theme.palette.background.default),
      }}
    >
      <Banner />

      <Container maxWidth="lg" sx={{ pt: 10, pb: 8 }}>
        <Stack spacing={3}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h4" component="h1" fontWeight="bold">
              Admin
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={() => navigate('/admin/discounts')}>
                Discounts and commissions
              </Button>
              <Button variant="contained" onClick={() => navigate('/admin/users')}>
                Manage users
              </Button>
            </Stack>
          </Stack>

          <Paper elevation={1} sx={{ p: 3 }}>
            <Stack spacing={2.5}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" component="h2">
                  Plans
                </Typography>

                {!isPlansLoading && !plansLoadError && !isPlansEditing ? (
                  <Button variant="outlined" onClick={handleEditPlans}>
                    Edit
                  </Button>
                ) : null}
              </Stack>

              {isPlansLoading ? (
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Loading plans...
                  </Typography>
                </Stack>
              ) : null}

              {!isPlansLoading && plansLoadError ? (
                <Stack spacing={1.5}>
                  <Alert severity="error">{plansLoadError}</Alert>
                  <Box>
                    <Button variant="outlined" onClick={() => void loadPlans()}>
                      Retry
                    </Button>
                  </Box>
                </Stack>
              ) : null}

              {!isPlansLoading && !plansLoadError ? (
                <Stack spacing={2.5}>
                  {plansDraft.map((plan) => {
                    const isFreePlan = plan.plan_code === 'free';
                    const sectionTitle = isFreePlan ? 'Free' : 'Pro';

                    return (
                      <Paper key={plan.plan_code} variant="outlined" sx={{ p: 2 }}>
                        <Stack spacing={1.5}>
                          <Typography variant="subtitle1" fontWeight={700}>
                            {sectionTitle}
                          </Typography>

                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                            <TextField
                              label="Max documents"
                              value={plan.max_documents}
                              onChange={(event) => handlePlanFieldChange(plan.plan_code, 'max_documents', event.target.value)}
                              fullWidth
                              size="small"
                              disabled={!isPlansEditing || isPlansSaving}
                            />
                            <TextField
                              label="Max T-accounts per document"
                              value={plan.max_taccounts_per_document}
                              onChange={(event) => handlePlanFieldChange(plan.plan_code, 'max_taccounts_per_document', event.target.value)}
                              fullWidth
                              size="small"
                              disabled={!isPlansEditing || isPlansSaving}
                            />
                            <TextField
                              label="Max transactions per document"
                              value={plan.max_transactions_per_document}
                              onChange={(event) => handlePlanFieldChange(plan.plan_code, 'max_transactions_per_document', event.target.value)}
                              fullWidth
                              size="small"
                              disabled={!isPlansEditing || isPlansSaving}
                            />
                          </Stack>

                          {!isFreePlan ? (
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                              <TextField
                                label="Monthly price (USD)"
                                value={plan.month_price_usd}
                                onChange={(event) => handlePlanFieldChange(plan.plan_code, 'month_price_usd', event.target.value)}
                                fullWidth
                                size="small"
                                disabled={!isPlansEditing || isPlansSaving}
                              />
                              <TextField
                                label="Yearly price (USD)"
                                value={plan.year_price_usd}
                                onChange={(event) => handlePlanFieldChange(plan.plan_code, 'year_price_usd', event.target.value)}
                                fullWidth
                                size="small"
                                disabled={!isPlansEditing || isPlansSaving}
                              />
                              <TextField
                                label="Free period days"
                                value={plan.free_trial_days}
                                onChange={(event) => handlePlanFieldChange(plan.plan_code, 'free_trial_days', event.target.value)}
                                fullWidth
                                size="small"
                                disabled={!isPlansEditing || isPlansSaving}
                              />
                            </Stack>
                          ) : null}
                        </Stack>
                      </Paper>
                    );
                  })}

                  {isPlansEditing ? (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      <Button
                        variant="outlined"
                        onClick={handleCancelPlansEdit}
                        disabled={isPlansSaving}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => {
                          void handleSavePlans();
                        }}
                        disabled={isPlansSaving}
                      >
                        {isPlansSaving ? 'Saving...' : 'Save'}
                      </Button>
                    </Box>
                  ) : null}
                </Stack>
              ) : null}
            </Stack>
          </Paper>

          <Paper elevation={1} sx={{ p: 3 }}>
            <Stack spacing={2.5}>
              <Typography variant="h6" component="h2">
                Runtime Flags
              </Typography>

              {isLoading ? (
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Loading runtime flags...
                  </Typography>
                </Stack>
              ) : null}

              {!isLoading && loadError ? (
                <Stack spacing={1.5}>
                  <Alert severity="error">{loadError}</Alert>
                  <Box>
                    <Button variant="outlined" onClick={() => void loadRuntimeFlags()}>
                      Retry
                    </Button>
                  </Box>
                </Stack>
              ) : null}

              {!isLoading && !loadError ? (
                <>
                  <Stack spacing={0.5}>
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={draftRequireWhitelistCookie}
                          onChange={(event) => setDraftRequireWhitelistCookie(event.target.checked)}
                          disabled={isSaving}
                        />
                      )}
                      label="Require whitelist cookie"
                    />

                    <Typography variant="body2" color="text.secondary">
                      When enabled, users must have the whitelist cookie to access the app.
                    </Typography>
                  </Stack>

                  {hasPendingChange ? (
                    <Box>
                      <Button variant="contained" onClick={handleApply} disabled={isSaving}>
                        {isSaving ? 'Applying...' : 'Apply'}
                      </Button>
                    </Box>
                  ) : null}
                </>
              ) : null}
            </Stack>
          </Paper>

          <Paper elevation={1} sx={{ p: 3 }}>
            <Stack spacing={2.5}>
              <Typography variant="h6" component="h2">
                Billing UAT Diagnostic
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Run a one-click readiness check for Stripe UAT configuration, pricing, and recent webhook health.
              </Typography>

              <Box>
                <Button
                  variant="contained"
                  onClick={() => void handleRunBillingDiagnostic()}
                  disabled={isDiagnosticLoading}
                >
                  {isDiagnosticLoading ? 'Running diagnostic...' : 'Run UAT Billing Diagnostic'}
                </Button>
              </Box>

              {diagnosticError ? <Alert severity="error">{diagnosticError}</Alert> : null}

              {diagnosticData ? (
                <Stack spacing={2}>
                  <Alert severity={diagnosticData.summary?.ready_for_uat ? 'success' : 'warning'}>
                    {diagnosticData.summary?.ready_for_uat
                      ? 'Billing UAT appears ready.'
                      : 'Billing UAT is not fully ready. Review failed checks below.'}
                  </Alert>

                  <Typography variant="body2" color="text.secondary">
                    Checked at: {diagnosticData.summary?.checked_at ? new Date(diagnosticData.summary.checked_at).toLocaleString() : 'Unknown'}
                  </Typography>

                  <Stack spacing={1.25}>
                    {Array.isArray(diagnosticData.checks)
                      ? diagnosticData.checks.map((check: any) => (
                        <Paper key={check.id} variant="outlined" sx={{ p: 1.5 }}>
                          <Stack spacing={0.5}>
                            <Typography variant="body2" fontWeight={700}>
                              {check.passed ? 'PASS' : 'FAIL'} - {check.label}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {check.detail}
                            </Typography>
                          </Stack>
                        </Paper>
                      ))
                      : null}
                  </Stack>

                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Stack spacing={0.5}>
                      <Typography variant="body2" fontWeight={700}>Webhook Activity (24h)</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Received: {diagnosticData.webhooks?.received_last_24h ?? 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Failed: {diagnosticData.webhooks?.failed_last_24h ?? 0}
                      </Typography>
                    </Stack>
                  </Paper>
                </Stack>
              ) : null}
            </Stack>
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
}
